import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { ArrowLeft } from "lucide-react";
import { DateInput } from "@/components/DateInput";
import { formatDate } from "@/lib/dates";
import { toast } from "sonner";
import { MatterDialog } from "@/components/forms";

const MATTER_STATUS_BADGE = {
  Active: "bg-green-50 text-green-700 border-green-200",
  Pending: "bg-amber-50 text-amber-800 border-amber-200",
  "On hold": "bg-sky-50 text-sky-800 border-sky-200",
  Closed: "bg-slate-100 text-slate-500 border-slate-200",
  Archived: "bg-slate-200 text-slate-500 border-slate-300",
};

const money = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2));
const thCls = "text-left text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-3 py-2";
const tdCls = "px-3 py-2 text-sm text-slate-700 border-t border-slate-100";

export default function ClientDetail() {
  const { t } = useT();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [matterFilter, setMatterFilter] = useState("");
  const [addMatterOpen, setAddMatterOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${id}`, {
        params: { q, date_from: dateFrom, date_to: dateTo },
      });
      setData(data);
    } catch (e) {
      setError(true);
    }
  }, [id, q, dateFrom, dateTo]);

  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);

  const toggleStatus = async () => {
    const next = (data?.client?.status || "Active") === "Inactive" ? "Active" : "Inactive";
    try {
      await api.patch(`/clients/${id}`, { status: next });
      toast.success(t("clients.status_updated"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (error) return <div className="text-sm text-red-600" data-testid="client-detail-error">404</div>;
  if (!data) return null;
  const { client, invoices, totals } = data;
  const rplaw_invoices = data.rplaw_invoices || [];
  const rplaw_totals = data.rplaw_totals || { billed: 0, paid: 0, outstanding: 0 };
  const clientMatters = data.matters || [];
  const importedFinancial = data.imported_financial || { confirmed_outstanding: 0, unknown_count: 0, unknown_amount: 0, paid_total: 0 };
  const filteredMatters = matterFilter ? clientMatters.filter((m) => m.status === matterFilter) : clientMatters;

  const field = (label, value, testid) => (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800 mt-0.5" data-testid={testid}>{value || "—"}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <Link to="/clients" data-testid="client-back-link"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors">
        <ArrowLeft className="h-4 w-4" /> {t("clients.back")}
      </Link>

      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="client-basic-data">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <h1 className="font-serif text-3xl text-slate-900" data-testid="client-detail-name">{client.name}</h1>
          <span
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${
              (client.status || "Active") === "Inactive"
                ? "bg-slate-100 text-slate-500 border-slate-200"
                : "bg-green-50 text-green-700 border-green-200"
            }`}
            data-testid="client-status-badge"
          >
            {(client.status || "Active") === "Inactive" ? t("val.Inactive") : t("val.Active")}
          </span>
          <button onClick={toggleStatus} data-testid="client-status-toggle"
            className="text-xs font-semibold text-amber-700 hover:underline">
            {(client.status || "Active") === "Inactive" ? t("clients.reactivate") : t("clients.mark_inactive")}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {field(t("import.col_reg"), client.registration_number, "client-field-reg")}
          {field(t("import.col_vat"), client.vat_number, "client-field-vat")}
          {field(t("import.col_country"), client.country, "client-field-country")}
          {field(t("import.col_legacy_id"), client.legacy_id, "client-field-legacy")}
          {field(t("clients.source"), client.source, "client-field-source")}
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="font-serif text-xl text-slate-800" data-testid="client-invoices-title">{t("clients.imported_invoices")}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="client-imp-financial">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{t("clients.imp_confirmed_outstanding")}</div>
            <div className="text-lg font-bold text-amber-700" data-testid="client-imp-outstanding">{money(importedFinancial.confirmed_outstanding)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{t("clients.imp_paid")}</div>
            <div className="text-lg font-bold text-green-700" data-testid="client-imp-paid">{money(importedFinancial.paid_total)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3 col-span-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{t("clients.imp_unknown")}</div>
            <div className="text-lg font-bold text-slate-500" data-testid="client-imp-unknown">
              {importedFinancial.unknown_count} · {money(importedFinancial.unknown_amount)}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("clients.filter_no")}
            data-testid="client-filter-no"
            className="px-3 py-2 border border-slate-200 rounded-md text-sm w-64 focus:outline-none focus:ring-2 focus:ring-slate-800" />
          <div className="w-40" title={t("clients.date_from")}>
            <DateInput testid="client-filter-date-from" value={dateFrom} onChange={setDateFrom} />
          </div>
          <div className="w-40" title={t("clients.date_to")}>
            <DateInput testid="client-filter-date-to" value={dateTo} onChange={setDateTo} />
          </div>
        </div>

        {invoices.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center bg-white border border-slate-200 rounded-lg" data-testid="client-invoices-empty">
            {t("clients.no_invoices")}
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="client-invoices-table">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className={thCls}>{t("import.col_invoice_no")}</th>
                  <th className={thCls}>{t("import.col_date")}</th>
                  <th className={thCls}>{t("import.col_type")}</th>
                  <th className={`${thCls} text-right`}>{t("import.col_taxable")}</th>
                  <th className={`${thCls} text-right`}>{t("import.col_vat_amount")}</th>
                  <th className={`${thCls} text-right`}>{t("import.col_gross")}</th>
                  <th className={thCls}>{t("import.col_currency")}</th>
                  <th className={`${thCls} text-right`}>{t("recv.col_outstanding")}</th>
                  <th className={thCls}>{t("recv.col_status")}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`client-invoice-row-${inv.invoice_no}`}>
                    <td className={tdCls}>
                      <Link to={`/imported-invoices/${inv.id}`} data-testid={`client-imp-link-${inv.invoice_no}`}
                        className="text-amber-700 hover:underline font-semibold">
                        {inv.invoice_no}
                      </Link>
                    </td>
                    <td className={tdCls}>{formatDate(inv.invoice_date)}</td>
                    <td className={tdCls}>{inv.invoice_type || "—"}</td>
                    <td className={`${tdCls} text-right`}>{money(inv.taxable_value)}</td>
                    <td className={`${tdCls} text-right`}>{money(inv.vat_amount)}</td>
                    <td className={`${tdCls} text-right`}>{money(inv.gross_total)}</td>
                    <td className={tdCls}>{inv.currency || "—"}</td>
                    <td className={`${tdCls} text-right`} data-testid={`client-imp-outstanding-${inv.invoice_no}`}>
                      {inv.payment_status === "unknown" ? "—" : money(inv.outstanding)}
                    </td>
                    <td className={tdCls}>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                        { unknown: "bg-slate-100 text-slate-500 border-slate-300",
                          unpaid: "bg-slate-100 text-slate-600 border-slate-200",
                          partial: "bg-amber-50 text-amber-800 border-amber-200",
                          paid: "bg-green-50 text-green-700 border-green-200",
                          overdue: "bg-red-50 text-red-700 border-red-200" }[inv.payment_status] || ""}`}
                        data-testid={`client-imp-status-${inv.invoice_no}`}>
                        {t(`pay.status.${inv.payment_status}`)}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-semibold" data-testid="client-invoices-totals">
                  <td className={tdCls} colSpan={3}>{t("clients.totals")}</td>
                  <td className={`${tdCls} text-right`} data-testid="client-total-taxable">{money(totals.taxable_value)}</td>
                  <td className={`${tdCls} text-right`} data-testid="client-total-vat">{money(totals.vat_amount)}</td>
                  <td className={`${tdCls} text-right`} data-testid="client-total-gross">{money(totals.gross_total)}</td>
                  <td className={tdCls}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-serif text-xl text-slate-800" data-testid="client-matters-title">{t("clients.matters")}</h2>
          <button onClick={() => setAddMatterOpen(true)} data-testid="client-add-matter-button"
            className="px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-md hover:bg-slate-800 transition-colors">
            {t("clients.add_matter")}
          </button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="client-matters-filters">
          {["", "Active", "On hold", "Closed", "Archived"].map((s) => (
            <button key={s || "all"} data-testid={`client-matters-filter-${(s || "all").toLowerCase().replace(" ", "-")}`}
              onClick={() => setMatterFilter(s)}
              className={`px-3 py-1 text-[11px] font-semibold rounded-full border transition-colors ${
                matterFilter === s
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}>
              {s === "" ? t("recv.all") : t(`val.${s}`)}
            </button>
          ))}
        </div>
        {filteredMatters.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center bg-white border border-slate-200 rounded-lg" data-testid="client-matters-empty">
            {t("clients.no_matters")}
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="client-matters-table">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className={thCls}>{t("clients.matter_name")}</th>
                  <th className={thCls}>{t("import.col_legacy_id") === "Source ID" || t("import.col_legacy_id") === "Šaltinio ID" ? t("matters.ref", { ref: "" }).replace(/\s*:\s*$/, "") : t("matters.ref", { ref: "" })}</th>
                  <th className={thCls}>{t("form.matter_type")}</th>
                  <th className={thCls}>{t("common.status")}</th>
                  <th className={thCls}>{t("form.case_number")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredMatters.map((m) => (
                  <tr key={m.id} data-testid={`client-matter-row-${m.id}`}>
                    <td className={tdCls}>
                      <Link to={`/matters/${m.id}`} data-testid={`client-matter-link-${m.id}`}
                        className="text-amber-700 hover:underline font-semibold">
                        {m.name}
                      </Link>
                    </td>
                    <td className={tdCls}>{m.internal_ref || "—"}</td>
                    <td className={tdCls}>{m.matter_type || "—"}</td>
                    <td className={tdCls}>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${MATTER_STATUS_BADGE[m.status] || ""}`}
                        data-testid={`client-matter-status-${m.id}`}>
                        {t(`val.${m.status}`)}
                      </span>
                    </td>
                    <td className={tdCls}>{m.court_case_number || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-serif text-xl text-slate-800" data-testid="client-rplaw-title">{t("clients.rplaw_invoices")}</h2>
        <div className="grid grid-cols-3 gap-3 max-w-lg" data-testid="client-rplaw-totals">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{t("clients.billed")}</div>
            <div className="text-lg font-bold text-slate-800" data-testid="client-rplaw-billed">{money(rplaw_totals.billed)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{t("clients.paid")}</div>
            <div className="text-lg font-bold text-green-700" data-testid="client-rplaw-paid">{money(rplaw_totals.paid)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{t("clients.outstanding")}</div>
            <div className="text-lg font-bold text-amber-700" data-testid="client-rplaw-outstanding">{money(rplaw_totals.outstanding)}</div>
          </div>
        </div>
        {rplaw_invoices.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center bg-white border border-slate-200 rounded-lg" data-testid="client-rplaw-empty">
            {t("clients.no_rplaw_invoices")}
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="client-rplaw-table">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className={thCls}>{t("import.col_invoice_no")}</th>
                  <th className={thCls}>{t("import.col_date")}</th>
                  <th className={`${thCls} text-right`}>{t("recv.col_gross")}</th>
                  <th className={`${thCls} text-right`}>{t("recv.col_paid")}</th>
                  <th className={`${thCls} text-right`}>{t("recv.col_outstanding")}</th>
                  <th className={thCls}>{t("recv.col_status")}</th>
                </tr>
              </thead>
              <tbody>
                {rplaw_invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`client-rplaw-row-${inv.invoice_number}`}>
                    <td className={tdCls}>
                      <Link to={`/invoices/${inv.id}`} data-testid={`client-rplaw-link-${inv.invoice_number}`}
                        className="text-amber-700 hover:underline font-semibold">
                        {inv.invoice_number}
                      </Link>
                    </td>
                    <td className={tdCls}>{formatDate(inv.invoice_date)}</td>
                    <td className={`${tdCls} text-right`}>{money(inv.gross)}</td>
                    <td className={`${tdCls} text-right`}>{money(inv.total_paid)}</td>
                    <td className={`${tdCls} text-right`}>{money(inv.outstanding)}</td>
                    <td className={tdCls}>{t(`pay.status.${inv.payment_status}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <MatterDialog
        open={addMatterOpen}
        onOpenChange={setAddMatterOpen}
        initial={{ client: client.name, client_id: client.id }}
        onSaved={(m) => {
          setAddMatterOpen(false);
          toast.success(t("matter.created"));
          load();
          if (m?.id) navigate(`/matters/${m.id}`);
        }}
      />
    </div>
  );
}
